import { createSlice } from '@reduxjs/toolkit'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'
import { heatModeSchema } from './heatModeSlice'
import { coolModeSchema } from './coolModeSlice'

const defaultForm = {
  id: null,
  uid: uuidv4(),
  header: null,
  forms: {
    heatModeStep: {
      data: null,
      form: null,
    },
    coolModeStep: {
      data: null,
      form: null,
    },
    hpChoiceStep: {
      data: null,
      form: null,
    },
  },
  quote: {
    lines: [],
    otherlines: [],
    vat: null,
    total: 0,
    totalHT: 0,
    comment: null,
  },
  simulationHot: true,
  simulationCool: false,
  deperds: null,
  flowTemperature: null,
  results: null,
  selectedProduct: null,
  schemaCode: null,
  accessories: null,
  currentStep: 0,
  maxReachedStep: 0,
  newMaximumTempTransmitter: null,
  selectedCompareProducts: [],
  selectedCompareProductIds: [],
}

const initialState = {
  traceId: typeof window !== 'undefined' ? localStorage.getItem('traceId') : null,
  minStep: 0,
  maxStep: 10,
  hasChanges: false,
  project:
    typeof window !== 'undefined' && localStorage.getItem('currentProject')
      ? JSON.parse(localStorage.getItem('currentProject') ?? '{}')
      : { ...defaultForm },
}

const initNewProjectReducer = (state: any) => {
  if (typeof window !== 'undefined') {
    const prev = localStorage.getItem('currentProject')
    if (prev) localStorage.setItem('lastProject', prev)
    localStorage.removeItem('currentProject')
  }
  state.project = { ...defaultForm, uid: uuidv4() }
  //state.traceId = null;
  removeTraceIdReducer(state)
}

const saveStoreReducer = (state: any) => {
  if (typeof window !== 'undefined') {
    state.hasChanges = JSON.stringify(state.project) !== localStorage.getItem('currentProject')
    localStorage.setItem('currentProject', JSON.stringify(state.project))
  }
}

const updateProjectIdReducer = (state: any, action: any) => {
  if (action.payload) {
    state.project.id = action.payload
  }
  saveStoreReducer(state)
  state.hasChanges = false;
}

const updateFormReducer = (state: any, action: any) => {
  const { form, data } = action.payload
  state.project.forms[form] = data
  saveStoreReducer(state)
}

// const nextStepReducer = (state: any, action: any) => {}

// const previousStepReducer = (state: any, action: any) => {}

// const goToStepReducer = (state: any, action: any) => {}

const setTraceIdReducer = (state: any, action: any) => {
  if (state) {
    state.traceId = action.payload;
  }
  if (typeof window !== 'undefined') {
    localStorage.setItem('traceId', action.payload)
  }
}
const removeTraceIdReducer = (state: any) => {
  if (state) {
    state.traceId = null;
  }
  if (typeof window !== 'undefined') {
    localStorage.removeItem('traceId')
  }
}

const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    initNewProject: initNewProjectReducer,
    saveStore: saveStoreReducer,
    updateProjectId: updateProjectIdReducer,
    updateForm: updateFormReducer,
    // nextStep: nextStepReducer,
    // previousStep: previousStepReducer,
    // goToStep: goToStepReducer,

    changeStep(state, action) {
      const index = action.payload
      state.project.currentStep = Math.max(state.minStep, Math.min(state.maxStep, index))
      state.project.maxReachedStep = Math.max(index, state.project.maxReachedStep)

      // if (index > 3 && state.traceId) {
      //   traceService.update();
      // }
    },
    selectProduct(state, action) {
      state.project.selectedProduct = action.payload
      state.project.accessories = null
    },
    setSchemaCode(state, action) {
      state.project.schemaCode = action.payload
    },
    setAccessories(state, action) {
      state.project.accessories = action.payload
    },
    load(state, action) {
      const project = action.payload
      state.project = { ...project.data, id: project.id }
      state.traceId = null
      saveStoreReducer(state)
      state.hasChanges = false;
    },
    setQuoteLines(state, action) {
      const lines = action.payload
      state.project.quote.lines = lines.filter((l: any) => !l._custom)
      state.project.quote.otherlines = lines.filter((l: any) => l._custom)

        // Update accessories props with change made in quote
        ;['recommendedAccessories', 'otherAccessories'].forEach((accType) => {
          state.project.accessories[accType].forEach((acc: any) => {
            const found = state.project.quote.lines
              .filter((qacc: any) => qacc._type === 'accessories')
              .find((qacc: any) => qacc.id === acc.id)
            if (found) {
              acc._selected = found._selected
              acc._qty = found._qty
            }
          })
        })
    },
    setVat(state, action) {
      state.project.quote.vat = action.payload
    },
    setQuoteTotal(state, action) {
      state.project.quote.total = action.payload
    },
    setQuoteTotalHT(state, action) {
      state.project.quote.totalHT = action.payload
    },
    setComment(state, action) {
      state.project.quote.comment = action.payload
    },
    setDeperds(state, action) {
      state.project.deperds = action.payload
    },
    setFlowTemperature(state, action) {
      state.project.flowTemperature = action.payload
    },
    mergeFlowTemperature(state, action) {
      if (!state.project.flowTemperature) state.project.flowTemperature = {}
      state.project.flowTemperature[`zone${action.payload.index + 1}`] = action.payload.data
    },
    resetResults(state) {
      state.project.results = null
    },
    setHeader(state, action) {
      state.project.header = action.payload
    },
    setNewTransmitterTemp(state, action) {
      state.project.newMaximumTempTransmitter = action.payload
    },
    setCoolMode(state, action) {
      state.project.simulationCool = action.payload
    },
    setTraceId: setTraceIdReducer,
    removeTraceId: removeTraceIdReducer,
    setProjectFieldValue(state, action) {
      state.project[action.payload.field] = action.payload.value;
    },
    setSelectedCompareProducts(state, action) {
      state.project.selectedCompareProducts = action.payload;
    },
    setSelectedCompareProductIds(state, action) {
      state.project.selectedCompareProductIds = action.payload;
    },
    setOtherLines(state, action) {
      state.project.quote.otherlines = action.payload;
    },
    setLines(state, action) {
      state.project.quote.lines = action.payload;
    }
  },
})

export const {
  initNewProject,
  saveStore,
  updateProjectId,
  updateForm,
  changeStep,
  selectProduct,
  setSchemaCode,
  setAccessories,
  load,
  setQuoteLines,
  setVat,
  setQuoteTotal,
  setQuoteTotalHT,
  setComment,
  resetResults,
  setHeader,
  setNewTransmitterTemp,
  setTraceId,
  removeTraceId,
  setDeperds,
  setFlowTemperature,
  mergeFlowTemperature,
  setProjectFieldValue,
  setCoolMode,
  setSelectedCompareProducts,
  setSelectedCompareProductIds,
  setOtherLines,
  setLines
} = projectSlice.actions

export const validationSchemaForForms = Yup.object({
  heatMode: heatModeSchema,
  coolMode: coolModeSchema,
  simulationCool: Yup.boolean().required(),
})

export type FormValues = Yup.InferType<typeof validationSchemaForForms>

export default projectSlice.reducer
